import { useFieldArray, useFormState, Controller } from 'react-hook-form';
import type { Control, FieldValues, FieldPath, ArrayPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Field, FieldError } from '@/shared/components/ui/field';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { TimeSelect } from '@/shared/components/ui/TimeSelect';
import { useUIStore } from '@/core/stores/ui.store';

interface WorkScheduleSegmentsFieldProps<T extends FieldValues> {
  control: Control<T>;
}

/**
 * Reusable `segments` array editor — one row per intraday segment, each with a
 * start/end `TimeSelect` and a remove button. Shared by the tenant and member
 * work-schedule forms; `order` is never edited here, it's derived from array
 * position when the payload is built for the API.
 */
export function WorkScheduleSegmentsField<T extends FieldValues>({
  control,
}: WorkScheduleSegmentsFieldProps<T>) {
  const { t } = useTranslation('workSchedules');
  const timeFormat = useUIStore((s) => s.timeFormat);
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'segments' as ArrayPath<T>,
  });
  const { errors } = useFormState({ control });
  const rootMessage = (errors as { segments?: { root?: { message?: string } } }).segments?.root
    ?.message;

  return (
    <div className="space-y-2" data-testid="work-schedule-segments-field">
      {fields.map((field, index) => (
        <div
          key={field.id}
          className="flex items-end gap-2"
          data-testid={`work-schedule-segment-row-${index}`}
        >
          <Controller
            name={`segments.${index}.startTime` as FieldPath<T>}
            control={control}
            render={({ field: startField, fieldState }) => (
              <Field data-invalid={fieldState.invalid} className="flex-1">
                <TimeSelect
                  value={(startField.value as string) || null}
                  onChange={startField.onChange}
                  timeFormat={timeFormat}
                  testId={`work-schedule-segment-${index}-start`}
                />
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          />

          <span className="pb-2 text-xs text-muted-foreground">–</span>

          <Controller
            name={`segments.${index}.endTime` as FieldPath<T>}
            control={control}
            render={({ field: endField, fieldState }) => (
              <Field data-invalid={fieldState.invalid} className="flex-1">
                <TimeSelect
                  value={(endField.value as string) || null}
                  onChange={endField.onChange}
                  timeFormat={timeFormat}
                  testId={`work-schedule-segment-${index}-end`}
                />
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => remove(index)}
                disabled={fields.length === 1}
                aria-label={t('segments.remove')}
                data-testid={`work-schedule-segment-remove-${index}`}
              >
                <Trash2 className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('segments.remove')}</TooltipContent>
          </Tooltip>
        </div>
      ))}

      {rootMessage && (
        <p className="text-sm text-destructive" data-testid="work-schedule-segments-error">
          {rootMessage}
        </p>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => append({ startTime: '', endTime: '' } as never)}
        data-testid="work-schedule-add-segment-btn"
      >
        <Plus className="size-4" />
        {t('segments.add')}
      </Button>
    </div>
  );
}
